import { FileText, Search, X } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { cn } from "@/lib/utils";
import type { WikiPage } from "@/services/types/matter-wiki-types";

interface WikiSearchProps {
  pages: WikiPage[];
  onSelectPage: (path: string) => void;
}

interface SearchResult {
  page: WikiPage;
  /** Snippet of matching content with the query highlighted */
  snippet: string | null;
  /** Whether the title matched */
  titleMatch: boolean;
}

const SNIPPET_RADIUS = 80;

/** Strip markdown syntax so snippets read as clean prose. */
function stripMarkdown(text: string): string {
  return (
    text
      // Remove citation references like [source:artifact:uuid...]
      .replace(/\[source:[^\]]*\]/g, "")
      // Remove markdown headings
      .replace(/^#{1,6}\s+/gm, "")
      // Remove bold/italic markers
      .replace(/\*{1,3}([^*]+)\*{1,3}/g, "$1")
      // Remove table separators
      .replace(/\|?\s*:?-+:?\s*\|/g, " ")
      // Remove remaining pipes (table cells)
      .replace(/\|/g, " ")
      // Remove link syntax [text](url)
      .replace(/\[([^\]]+)\]\([^)]+\)/g, "$1")
      // Remove inline code backticks
      .replace(/`([^`]+)`/g, "$1")
      // Collapse whitespace
      .replace(/\s+/g, " ")
      .trim()
  );
}

function buildSnippet(content: string, query: string): string | null {
  const cleaned = stripMarkdown(content);
  const lowerContent = cleaned.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const idx = lowerContent.indexOf(lowerQuery);
  if (idx === -1) return null;

  const start = Math.max(0, idx - SNIPPET_RADIUS);
  const end = Math.min(cleaned.length, idx + query.length + SNIPPET_RADIUS);

  const prefix = start > 0 ? "\u2026" : "";
  const suffix = end < cleaned.length ? "\u2026" : "";

  return `${prefix}${cleaned.slice(start, end)}${suffix}`;
}

function highlightMatch(text: string, query: string): React.ReactNode[] {
  if (!query) return [text];

  const lowerText = text.toLowerCase();
  const lowerQuery = query.toLowerCase();
  const parts: React.ReactNode[] = [];
  let cursor = 0;

  let matchIdx = lowerText.indexOf(lowerQuery, cursor);
  while (matchIdx !== -1) {
    if (matchIdx > cursor) {
      parts.push(text.slice(cursor, matchIdx));
    }
    parts.push(
      <mark
        key={matchIdx}
        className="bg-yellow-100 text-yellow-900 rounded-sm px-0.5"
      >
        {text.slice(matchIdx, matchIdx + query.length)}
      </mark>,
    );
    cursor = matchIdx + query.length;
    matchIdx = lowerText.indexOf(lowerQuery, cursor);
  }

  if (cursor < text.length) {
    parts.push(text.slice(cursor));
  }

  return parts;
}

export const WikiSearch: React.FC<WikiSearchProps> = ({
  pages,
  onSelectPage,
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // -- Search results -------------------------------------------------------

  const results: SearchResult[] = useMemo(() => {
    const trimmed = query.trim();
    if (trimmed.length < 2) return [];

    const lowerQuery = trimmed.toLowerCase();

    return pages
      .map((page) => {
        const titleMatch = page.title.toLowerCase().includes(lowerQuery);
        const snippet = buildSnippet(page.content, trimmed);
        if (!titleMatch && !snippet) return null;
        return { page, snippet, titleMatch } as SearchResult;
      })
      .filter((r): r is SearchResult => r !== null)
      .sort((a, b) => {
        // Title matches first, then by position
        if (a.titleMatch && !b.titleMatch) return -1;
        if (!a.titleMatch && b.titleMatch) return 1;
        return a.page.position - b.page.position;
      })
      .slice(0, 12);
  }, [query, pages]);

  const hasResults = results.length > 0;
  const showDropdown = isOpen && query.trim().length >= 2;

  // -- Keyboard navigation --------------------------------------------------

  useEffect(() => {
    setSelectedIndex(0);
  }, [results]);

  // Scroll selected item into view
  useEffect(() => {
    if (!listRef.current) return;
    const selected = listRef.current.children[selectedIndex] as
      | HTMLElement
      | undefined;
    if (selected) {
      selected.scrollIntoView({ block: "nearest" });
    }
  }, [selectedIndex]);

  const handleSelect = useCallback(
    (path: string) => {
      onSelectPage(path);
      setQuery("");
      setIsOpen(false);
      inputRef.current?.blur();
    },
    [onSelectPage],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (!showDropdown || !hasResults) return;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, results.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        const result = results[selectedIndex];
        if (result) handleSelect(result.page.path);
      } else if (e.key === "Escape") {
        e.preventDefault();
        setIsOpen(false);
        inputRef.current?.blur();
      }
    },
    [showDropdown, hasResults, results, selectedIndex, handleSelect],
  );

  // -- Click outside --------------------------------------------------------

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // -- Cmd+F / Ctrl+F shortcut ---------------------------------------------

  useEffect(() => {
    const handleGlobalKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "f") {
        // Only capture if we're inside the wiki context
        const wikiEl = containerRef.current?.closest(
          "[data-wiki-container]",
        );
        if (wikiEl) {
          e.preventDefault();
          inputRef.current?.focus();
          inputRef.current?.select();
        }
      }
    };
    window.addEventListener("keydown", handleGlobalKeyDown);
    return () => window.removeEventListener("keydown", handleGlobalKeyDown);
  }, []);

  return (
    <div ref={containerRef} className="relative">
      <div
        className={cn(
          "flex items-center gap-2 rounded-lg border px-2.5 py-1.5",
          "transition-all duration-150",
          isOpen
            ? "border-zinc-300 bg-white shadow-sm"
            : "border-zinc-200 bg-zinc-50 hover:border-zinc-300 hover:bg-white",
        )}
      >
        <Search className="h-3.5 w-3.5 text-zinc-400 shrink-0" />
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => setIsOpen(true)}
          onKeyDown={handleKeyDown}
          placeholder="Search pages..."
          className="flex-1 bg-transparent text-xs text-zinc-700 placeholder:text-zinc-400 outline-none"
        />
        {query && (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              inputRef.current?.focus();
            }}
            className="text-zinc-400 hover:text-zinc-600 transition-colors"
          >
            <X className="h-3 w-3" />
          </button>
        )}
      </div>

      {/* Results dropdown */}
      {showDropdown && (
        <div
          className={cn(
            "absolute top-full left-0 right-0 mt-1.5 z-50",
            "rounded-xl border border-zinc-200 bg-white shadow-xl shadow-zinc-200/50",
            "max-h-[420px] overflow-y-auto",
            "[&::-webkit-scrollbar]:w-1.5",
            "[&::-webkit-scrollbar-track]:bg-transparent",
            "[&::-webkit-scrollbar-thumb]:bg-zinc-200 [&::-webkit-scrollbar-thumb]:rounded-full",
          )}
        >
          {hasResults ? (
            <div ref={listRef} className="py-1.5">
              {results.map((result, idx) => {
                const pathSegments = result.page.path
                  .split("/")
                  .filter(Boolean)
                  .slice(0, -1);

                return (
                  <button
                    key={result.page.path}
                    type="button"
                    onClick={() => handleSelect(result.page.path)}
                    onMouseEnter={() => setSelectedIndex(idx)}
                    className={cn(
                      "flex w-full items-start gap-3 px-4 py-3 text-left cursor-pointer",
                      "transition-colors duration-100",
                      idx === selectedIndex
                        ? "bg-zinc-50"
                        : "hover:bg-zinc-50/60",
                      idx > 0 && "border-t border-zinc-100",
                    )}
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-zinc-100 shrink-0 mt-0.5">
                      <FileText className="h-4 w-4 text-zinc-400" />
                    </div>
                    <div className="min-w-0 flex-1">
                      {pathSegments.length > 0 && (
                        <p className="text-[10px] text-zinc-400 mb-0.5 tracking-wide">
                          {pathSegments.join(" \u203A ")}
                        </p>
                      )}
                      <p className="text-[13px] font-medium text-zinc-900 truncate">
                        {highlightMatch(result.page.title, query.trim())}
                      </p>
                      {result.snippet && (
                        <p className="text-xs text-zinc-400 mt-1 line-clamp-2 leading-relaxed">
                          {highlightMatch(result.snippet, query.trim())}
                        </p>
                      )}
                    </div>
                  </button>
                );
              })}
            </div>
          ) : (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-zinc-400">No pages found</p>
              <p className="text-xs text-zinc-300 mt-1">
                Try a different search term
              </p>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
